import { forwardRef } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { OnboardingLayoutProps } from '@/types/components/onboarding'
import { useBrand, useIsAppBranded } from '@/lib/brand-provider'
import { UiLanguageSwitcher } from '@/components/auth/UiLanguageSwitcher'
import { useTranslation } from 'react-i18next'

export const OnboardingLayout = forwardRef<HTMLDivElement, OnboardingLayoutProps>(
  function OnboardingLayout(
    {
      children,
      className,
      maxWidth,
      contentClassName,
      topLeft,
      progress,
    },
    ref,
  ) {
    const { t } = useTranslation(['onboarding', 'common'])
    const { brandIconLogoUrl } = useBrand()
    const isAppBranded = useIsAppBranded()

    const logoSrc = isAppBranded && brandIconLogoUrl ? brandIconLogoUrl : '/twins-app-icon.svg'
    const logoAlt = isAppBranded && brandIconLogoUrl ? t('logoAltOrg', { ns: 'common' }) : t('logoAltTwins', { ns: 'common' })

    return (
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={t('ariaLabel')}
        data-testid="onboarding-layout"
        className={cn('fixed inset-0 z-50 flex min-h-dvh flex-col bg-background', className)}
      >
        {/* Top progress bar — uses brand primary when white-label is active, mint otherwise */}
        {progress !== undefined && (
          <div className={cn('absolute inset-x-0 top-0 z-20 h-[3px]', isAppBranded ? 'bg-primary/20' : 'bg-mint/20')}>
            <motion.div
              className={cn('h-full rounded-r-full', isAppBranded ? 'bg-primary' : 'bg-mint')}
              initial={false}
              animate={{ width: `${Math.min(progress, 1) * 100}%` }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
            />
          </div>
        )}

        {topLeft && (
          <div className="absolute left-4 top-4 z-10">
            {topLeft}
          </div>
        )}
        <div className="absolute right-4 top-4 z-10">
          <UiLanguageSwitcher />
        </div>
        <div className={cn('flex items-center justify-center px-4 py-3', progress !== undefined && 'pt-5')}>
          <img
            src={logoSrc}
            alt={logoAlt}
            className="h-8"
          />
        </div>

        <div className={cn('flex flex-1 flex-col overflow-y-auto', contentClassName ?? 'px-6 py-8')}>
          <div className={cn('mx-auto my-auto w-full', maxWidth ?? 'max-w-md')}>
            {children}
          </div>
        </div>
      </div>
    )
  },
)
